import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import BookingFormModal from './BookingFormModal';
import { updateUserAchievements } from '../../../utils/badgeUnlockLogic';
import notificationService from '../../../utils/notificationService';
import offlineStorage from '../../../utils/offlineStorage';
import { useAuth } from '../../../contexts/AuthContext';

const BookingPanel = ({ pricing, onBook, eventId, eventStatus = 'upcoming', capacity = 15, currentBookings = 0, eventOptions }) => {
  const [ticketType, setTicketType] = useState(null); // null = not yet selected
  const [quantity, setQuantity] = useState(1);
  const [bookingStatus, setBookingStatus] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [isWaitlist, setIsWaitlist] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Derive enabled ticket options from eventOptions prop
  const allowOwnBoard = eventOptions?.allowOwnBoard !== false;
  const allowRentalBoard = eventOptions?.allowRentalBoard !== false;
  const allowStandardTicket = eventOptions?.allowStandardTicket === true;
  const priceStandardTicket = eventOptions?.priceStandardTicket || 0;

  // Build list of enabled options
  const enabledOptions = [
    allowOwnBoard && { key: 'own', label: 'Kendi Boardumu Getiriyorum', desc: eventOptions?.descOwnBoard || '', price: pricing?.ownBoard || 0, icon: 'Package', color: 'primary' },
    allowRentalBoard && { key: 'rental', label: 'Kiralık Board Dahil', desc: eventOptions?.descRentalBoard || '', price: pricing?.rental || 0, icon: 'ShoppingBag', color: 'secondary' },
    allowStandardTicket && { key: 'standard', label: 'Standart Etkinlik Bileti', desc: eventOptions?.descStandardTicket || '', price: priceStandardTicket, icon: 'Ticket', color: 'warning' },
  ]?.filter(Boolean);

  // Auto-select if only one option is enabled
  useEffect(() => {
    if (enabledOptions?.length === 1 && !ticketType) {
      setTicketType(enabledOptions?.[0]?.key);
    }
  }, [enabledOptions?.length]);

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const bookings = JSON.parse(localStorage.getItem('bookings') || '[]');
    const userBooking = bookings?.find(
      (booking) => booking?.eventId === eventId && booking?.userId === currentUser?.id
    );
    if (userBooking) {
      setBookingStatus('joined');
    } else if (eventStatus === 'completed' || eventStatus === 'past') {
      setBookingStatus('completed');
    }
    if (currentBookings >= capacity) {
      setIsWaitlist(true);
    }
  }, [eventId, eventStatus, capacity, currentBookings]);

  const selectedOption = enabledOptions?.find(o => o?.key === ticketType);
  const basePrice = selectedOption?.price || pricing?.ownBoard || pricing?.basePrice || 0;
  const totalPrice = basePrice * quantity;

  const handleJoinClick = () => {
    if (!user) {
      navigate('/login', { state: { from: `/event-details/${eventId}` } });
      return;
    }
    if (isWaitlist) {
      handleWaitlistJoin();
    } else {
      setShowBookingModal(true);
    }
  };

  const handleWaitlistJoin = () => {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const waitlist = JSON.parse(localStorage.getItem('waitlist') || '[]');
    const newWaitlistEntry = { id: Date.now(), eventId, userId: currentUser?.id, joinedAt: new Date()?.toISOString() };
    waitlist?.push(newWaitlistEntry);
    localStorage.setItem('waitlist', JSON.stringify(waitlist));
    setBookingStatus('waitlist');
    onBook({ status: 'waitlist' });
  };

  const handleConfirmBooking = (bookingData) => {
    setBookingStatus('joined');
    onBook(bookingData);
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    if (currentUser?.id) {
      updateUserAchievements(currentUser?.id);
    }
    offlineStorage?.saveBooking({ id: Date.now(), eventId, userId: currentUser?.id, ...bookingData, timestamp: new Date()?.toISOString() });
    notificationService?.showNotification('✅ Rezervasyon Onaylandı!', { body: 'Etkinlik biletiniz dijital biletlerinize eklendi.', tag: 'booking', priority: 'medium' });
  };

  const renderBookingButton = () => {
    if (bookingStatus === 'joined') {
      return <Button variant="outline" size="lg" fullWidth iconName="CheckCircle" iconPosition="left" disabled className="bg-muted text-muted-foreground cursor-not-allowed">Katıldınız</Button>;
    }
    if (bookingStatus === 'waitlist') {
      return <Button variant="outline" size="lg" fullWidth iconName="Clock" iconPosition="left" disabled className="bg-muted text-muted-foreground cursor-not-allowed">Yedek Listede</Button>;
    }
    if (bookingStatus === 'completed') {
      return <Button variant="outline" size="lg" fullWidth iconName="Check" iconPosition="left" disabled className="bg-muted text-muted-foreground cursor-not-allowed">Tamamlandı</Button>;
    }
    if (isWaitlist) {
      return <Button variant="secondary" size="lg" fullWidth iconName="UserPlus" iconPosition="left" onClick={handleJoinClick}>Yedek Listeye Katıl</Button>;
    }
    return (
      <Button
        variant="default"
        size="lg"
        fullWidth
        iconName="Calendar"
        iconPosition="left"
        onClick={handleJoinClick}
        disabled={enabledOptions?.length > 1 && !ticketType}
      >
        {enabledOptions?.length > 1 && !ticketType ? 'Bilet Tipi Seçin' : 'Katıl'}
      </Button>
    );
  };

  return (
    <>
      <div className="bg-card rounded-xl md:rounded-2xl p-4 md:p-6 border border-border sticky top-20">
        <div className="mb-6">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-3xl md:text-4xl font-bold text-foreground">
              ₺{enabledOptions?.[0]?.price || pricing?.ownBoard || pricing?.basePrice || 0}
            </span>
            <span className="text-sm text-muted-foreground">/ kişi</span>
          </div>
          <p className="text-sm text-muted-foreground">Başlangıç fiyatı</p>
          {isWaitlist && (
            <div className="mt-2 flex items-center gap-2 text-warning">
              <Icon name="AlertCircle" size={16} />
              <span className="text-sm font-medium">Kapasite Doldu</span>
            </div>
          )}
        </div>

        {!bookingStatus && enabledOptions?.length > 0 && (
          <div className="space-y-4 mb-6">
            {/* Only show selector if more than 1 option */}
            {enabledOptions?.length > 1 && (
              <div>
                <label className="text-sm font-medium text-foreground mb-3 block">Bilet Tipi Seçin</label>
                <div className="space-y-2">
                  {enabledOptions?.map((opt) => (
                    <button
                      key={opt?.key}
                      onClick={() => setTicketType(opt?.key)}
                      className={`w-full p-4 rounded-lg border-2 transition-all text-left ${ticketType === opt?.key ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Icon name={opt?.icon} size={18} className="text-primary" />
                            <span className="font-medium text-foreground">{opt?.label}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{opt?.desc}</p>
                        </div>
                        <span className="text-lg font-semibold text-foreground ml-2">₺{opt?.price}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Single option: show as info card (auto-selected, no radio needed) */}
            {enabledOptions?.length === 1 && (
              <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name={enabledOptions?.[0]?.icon} size={16} className="text-primary" />
                  <span className="text-sm font-semibold text-foreground">{enabledOptions?.[0]?.label}</span>
                </div>
                <p className="text-xs text-muted-foreground">{enabledOptions?.[0]?.desc}</p>
                <p className="text-sm font-bold text-primary mt-1">₺{enabledOptions?.[0]?.price}</p>
              </div>
            )}
          </div>
        )}

        {renderBookingButton()}

        <div className="mt-4 p-3 bg-muted/50 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              "Rezervasyon talebiniz ön başvuru olarak alınacaktır. Kesin kayıt onayınız, başvuru sırası ve kontenjan uygunluğu doğrultusunda tarafınıza ayrıca bildirilecektir."
            </p>
          </div>
        </div>
      </div>
      <BookingFormModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        eventId={eventId}
        pricing={pricing}
        capacity={capacity}
        currentBookings={currentBookings}
        onConfirmBooking={handleConfirmBooking}
        selectedTicketType={ticketType}
        eventOptions={eventOptions}
      />
    </>
  );
};

export default BookingPanel;